import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";

const Privacy = () => {
  return (
    <div className="min-h-screen bg-background py-12 px-4">
      <div className="container mx-auto max-w-4xl">
        <h1 className="text-4xl font-bold mb-8">Privacy Policy</h1>
        <p className="text-muted-foreground mb-8">Last Updated: November 10, 2025</p>

        <div className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>1. Introduction</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <p>
                Vocalizing ("we," "our," or "us") is committed to protecting your privacy. This Privacy Policy explains 
                how we collect, use, disclose, and safeguard your information when you use our Platform.
              </p>
              <p>
                By using Vocalizing, you consent to the data practices described in this policy.
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>2. Information We Collect</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <p><strong>2.1 Personal Information:</strong></p>
              <ul className="list-disc pl-6 space-y-2">
                <li>Name and email address (required for account creation)</li>
                <li>Profile information (optional: avatar, bio, experience)</li>
                <li>Contact information (phone number for coaches)</li>
                <li>Payment information (processed securely by Stripe)</li>
              </ul>
              
              <p><strong>2.2 Booking Information:</strong></p>
              <ul className="list-disc pl-6 space-y-2">
                <li>Lesson dates, times, and durations</li>
                <li>Video conference access links</li>
                <li>Booking status and payment records</li>
              </ul>

              <p><strong>2.3 Coach Application Data:</strong></p>
              <ul className="list-disc pl-6 space-y-2">
                <li>Professional qualifications and experience</li>
                <li>Teaching philosophy and expertise</li>
                <li>Application status and admin reviews</li>
              </ul>

              <p><strong>2.4 Usage Data:</strong></p>
              <ul className="list-disc pl-6 space-y-2">
                <li>Browser type and version</li>
                <li>IP address and device information</li>
                <li>Pages visited and time spent on the Platform</li>
                <li>Session data and authentication tokens</li>
              </ul>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>3. How We Use Your Information</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <p>We use your information to:</p>
              <ul className="list-disc pl-6 space-y-2">
                <li><strong>Provide Services:</strong> Create and manage your account, process bookings, facilitate lessons</li>
                <li><strong>Payment Processing:</strong> Handle payments securely through our payment processor (Stripe)</li>
                <li><strong>Communications:</strong> Send booking confirmations, reminders, and important platform updates</li>
                <li><strong>Improve Platform:</strong> Analyze usage patterns to enhance user experience and features</li>
                <li><strong>Security:</strong> Detect and prevent fraud, abuse, and security incidents</li>
                <li><strong>Coach Verification:</strong> Review and approve coach applications</li>
                <li><strong>Customer Support:</strong> Respond to inquiries and resolve issues</li>
              </ul>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>4. Information Sharing and Disclosure</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <p><strong>4.1 With Other Users:</strong></p>
              <ul className="list-disc pl-6 space-y-2">
                <li>Students can view coach profiles and public information</li>
                <li>Coaches can view student names and contact information for confirmed bookings</li>
                <li>Video conference links are shared between matched students and coaches</li>
              </ul>

              <p><strong>4.2 With Service Providers:</strong></p>
              <ul className="list-disc pl-6 space-y-2">
                <li><strong>Stripe:</strong> Payment processing (subject to Stripe's privacy policy)</li>
                <li><strong>Supabase:</strong> Database and authentication services</li>
                <li><strong>Email Services:</strong> Transactional email delivery (Resend)</li>
                <li><strong>Video Conferencing:</strong> Online lesson delivery</li>
              </ul>

              <p><strong>4.3 Legal Requirements:</strong></p>
              <p>We may disclose your information if required by law or to protect our rights, safety, or property.</p>

              <p><strong>4.4 We DO NOT:</strong></p>
              <ul className="list-disc pl-6 space-y-2">
                <li>Sell your personal information to third parties</li>
                <li>Share your data for marketing purposes without consent</li>
                <li>Disclose sensitive information beyond what's necessary for platform operation</li>
              </ul>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>5. Data Security</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <p><strong>5.1 Security Measures:</strong></p>
              <ul className="list-disc pl-6 space-y-2">
                <li>Encrypted data transmission (SSL/TLS)</li>
                <li>Secure authentication and session management</li>
                <li>Row-level security policies on database tables</li>
                <li>Role-based access control for sensitive data</li>
                <li>Regular security audits and updates</li>
              </ul>

              <p><strong>5.2 Payment Security:</strong></p>
              <p>
                Payment information is processed by Stripe and never stored on our servers. Stripe is PCI-DSS compliant 
                and uses industry-leading security measures.
              </p>

              <p><strong>5.3 Your Responsibility:</strong></p>
              <p>
                You are responsible for maintaining the confidentiality of your account credentials. Never share your 
                password with others.
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>6. Data Retention</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <p>We retain your information for as long as:</p>
              <ul className="list-disc pl-6 space-y-2">
                <li>Your account is active</li>
                <li>Needed to provide services</li>
                <li>Required to comply with legal obligations</li>
                <li>Necessary to resolve disputes or enforce agreements</li>
              </ul>
              <p>
                When you delete your account, we will delete or anonymize your personal information, except where we 
                must retain it for legal or legitimate business purposes.
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>7. Your Privacy Rights</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <p>You have the right to:</p>
              <ul className="list-disc pl-6 space-y-2">
                <li><strong>Access:</strong> Request a copy of your personal data</li>
                <li><strong>Correction:</strong> Update inaccurate or incomplete information</li>
                <li><strong>Deletion:</strong> Request deletion of your account and data</li>
                <li><strong>Portability:</strong> Receive your data in a structured, machine-readable format</li>
                <li><strong>Objection:</strong> Object to certain processing of your data</li>
                <li><strong>Restriction:</strong> Request restriction of data processing in certain circumstances</li>
              </ul>
              <p>
                To exercise these rights, please contact us through the Platform or email support@vocalizing.com.
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>8. Cookies and Tracking</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <p><strong>8.1 Essential Cookies:</strong></p>
              <p>
                We use essential cookies for authentication and session management. These are necessary for the Platform to function.
              </p>

              <p><strong>8.2 Local Storage:</strong></p>
              <p>
                We use browser local storage to maintain your authentication session and improve user experience.
              </p>

              <p><strong>8.3 Analytics:</strong></p>
              <p>
                We may use analytics tools to understand how users interact with our Platform and improve our services.
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>9. Children's Privacy</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <p>
                Our Platform is not intended for children under 13. We do not knowingly collect personal information 
                from children under 13. If a parent or guardian becomes aware that their child has provided us with 
                information without their consent, please contact us, and we will delete such information.
              </p>
              <p>
                For users aged 13-17, parental consent may be required depending on local regulations.
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>10. International Data Transfers</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <p>
                Your information may be transferred to and maintained on servers located outside your country. 
                We ensure appropriate safeguards are in place to protect your data in accordance with this Privacy Policy.
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>11. Changes to This Privacy Policy</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <p>
                We may update this Privacy Policy from time to time. We will notify you of any material changes by 
                posting the new policy on this page and updating the "Last Updated" date. Your continued use of the 
                Platform after changes constitutes acceptance of the updated policy.
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>12. Contact Us</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <p>
                If you have questions or concerns about this Privacy Policy or our data practices, please contact us at:
              </p>
              <ul className="list-none space-y-2">
                <li><strong>Email:</strong> privacy@vocalizing.com</li>
                <li><strong>Support:</strong> support@vocalizing.com</li>
              </ul>
              <p>
                We will respond to your inquiry within a reasonable timeframe.
              </p>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default Privacy;
